// Lógica de la pantalla Gestión de permisos obras (sin React, para probarla sola).
// Contrato: book-bsk-api/BookBSK/Controllers/ObrasPermisosController.cs.

export const EMPRESA_ATLANTE = 1;
export const PERFIL_ADMINISTRADOR = 1;

export const PERFILES = { 1: "Administrador", 0: "Usuario" };

export const VALORES_ESPECIALES = {
    "@empresa": "Su propia empresa",
    "@usuario": "El propio usuario",
};

// Los administradores de Grupo Atlante tienen siempre acceso total: no se asignan.
export const esAdministradorAtlante = (grupo) =>
    grupo.codEmpresa === EMPRESA_ATLANTE && grupo.codPerfil === PERFIL_ADMINISTRADOR;

const tieneAcceso = (grupo) => grupo.codConfiguracion != null || esAdministradorAtlante(grupo);

// Grupos con usuarios y sin configuración, de cualquier empresa y perfil: sus usuarios no
// entran en Obras (p. ej. una empresa nueva o un perfil que nunca se asignó).
export const gruposSinAcceso = (grupos) =>
    grupos.filter((grupo) => !tieneAcceso(grupo) && grupo.usuarios > 0);

export const VISTAS_GRUPOS = [
    { id: "relevantes", label: "Con usuarios o con acceso" },
    { id: "conAcceso", label: "Con acceso" },
    { id: "sinAcceso", label: "Con usuarios y sin acceso" },
    { id: "todas", label: "Todas las empresas" },
];

// Tipos de empresa presentes en los grupos, para filtrar por cualquiera de ellos.
export function tiposDeEmpresa(grupos) {
    const tipos = new Map();
    grupos.forEach((grupo) => {
        if (grupo.codTipoEmpresa != null && !tipos.has(grupo.codTipoEmpresa))
            tipos.set(grupo.codTipoEmpresa, grupo.tipoEmpresa || `Tipo ${grupo.codTipoEmpresa}`);
    });
    return [...tipos].map(([cod, nombre]) => ({ cod, nombre })).sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
}

const normalizar = (texto) => String(texto ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// tipo: codTipoEmpresa, o "" para todos.
export function filtrarGrupos(grupos, { texto = "", vista = "relevantes", tipo = "" } = {}) {
    const buscado = normalizar(texto.trim());
    return grupos.filter((grupo) => {
        if (buscado && !normalizar(`${grupo.empresa} ${grupo.tipoEmpresa || ""}`).includes(buscado)) return false;
        if (tipo !== "" && String(grupo.codTipoEmpresa) !== String(tipo)) return false;
        switch (vista) {
            case "conAcceso": return tieneAcceso(grupo);
            case "sinAcceso": return !tieneAcceso(grupo) && grupo.usuarios > 0;
            case "todas": return true;
            default: return tieneAcceso(grupo) || grupo.usuarios > 0;
        }
    });
}

// Grupos que tienen asignada una configuración, para enseñarlos en su ficha.
export const gruposDeConfiguracion = (grupos, codConfiguracion) =>
    grupos.filter((grupo) => grupo.codConfiguracion === codConfiguracion);

export const nuevaConfiguracion = () => ({
    codConfiguracion: 0,
    nombre: "",
    descripcion: "",
    editaTodo: false,
    puedeCrear: false,
    puedeArchivar: false,
    puedeCatalogos: false,
    campos: [],
    filtros: {},
    grupos: 0,
});

// Qué edita: "nada" (consulta), "todo" o "campos" (una selección). _modo guarda la elección
// de la pantalla mientras «solo estos campos» aún no tiene ninguno marcado.
export const modoEdicion = (configuracion) =>
    configuracion._modo || (configuracion.editaTodo ? "todo" : configuracion.campos.length ? "campos" : "nada");

export function conModoEdicion(configuracion, modo) {
    if (modo === "todo") return { ...configuracion, _modo: modo, editaTodo: true, campos: [] };
    if (modo === "nada") return { ...configuracion, _modo: modo, editaTodo: false, campos: [] };
    return { ...configuracion, _modo: modo, editaTodo: false };
}

export function alternarCampos(configuracion, claves, marcar) {
    const campos = new Set(configuracion.campos);
    claves.forEach((clave) => (marcar ? campos.add(clave) : campos.delete(clave)));
    return { ...configuracion, campos: [...campos] };
}

export function ponerFiltro(configuracion, dimension, valores) {
    const filtros = { ...configuracion.filtros };
    if (valores === undefined) delete filtros[dimension];
    else filtros[dimension] = [...new Set(valores)];
    return { ...configuracion, filtros };
}

// Error que impide guardar, o "" si se puede.
export function validarConfiguracion(configuracion) {
    if (!configuracion.nombre.trim()) return "Ponle un nombre a la configuración.";
    if (configuracion.nombre.trim().length > 80) return "El nombre no puede pasar de 80 caracteres.";
    if ((configuracion.descripcion || "").trim().length > 300) return "La descripción no puede pasar de 300 caracteres.";
    const vacio = Object.entries(configuracion.filtros).find(([, valores]) => !valores.length);
    if (vacio) return "Hay una condición de «qué obras ve» sin ningún valor: elige alguno o quítala.";
    if (modoEdicion(configuracion) === "campos" && !configuracion.campos.length)
        return "Marca algún campo editable o elige «Nada (solo consulta)».";
    return "";
}

// Cuerpo para el API.
export const aPeticion = (configuracion) => ({
    nombre: configuracion.nombre.trim(),
    descripcion: (configuracion.descripcion || "").trim() || null,
    editaTodo: configuracion.editaTodo,
    puedeCrear: configuracion.puedeCrear,
    puedeArchivar: configuracion.puedeArchivar,
    puedeCatalogos: configuracion.puedeCatalogos,
    campos: configuracion.editaTodo ? [] : [...configuracion.campos].sort(),
    filtros: Object.fromEntries(Object.entries(configuracion.filtros).filter(([, valores]) => valores.length)),
});

// ¿Hay cambios sin guardar respecto a la guardada?
export const tieneCambios = (borrador, guardada) =>
    JSON.stringify(aPeticion(borrador)) !== JSON.stringify(aPeticion(guardada || nuevaConfiguracion()));

export function textoValor(dimension, valor) {
    if (VALORES_ESPECIALES[valor]) return VALORES_ESPECIALES[valor];
    const opcion = dimension?.opciones?.find((item) => item.valor === valor);
    return opcion ? opcion.texto : `#${valor}`;
}

// Resumen de una línea de cada parte, para la lista de configuraciones.
export function resumenConfiguracion(configuracion, dimensiones = []) {
    const porClave = Object.fromEntries(dimensiones.map((dimension) => [dimension.clave, dimension]));
    const condiciones = Object.entries(configuracion.filtros || {});
    const ve = condiciones.length
        ? condiciones.map(([clave, valores]) => `${porClave[clave]?.etiqueta || clave}: ${valores.map((valor) => textoValor(porClave[clave], valor)).join(", ")}`).join(" · ")
        : "Todas las obras";
    const modo = modoEdicion(configuracion);
    const edita = modo === "todo" ? "Todos los campos" : modo === "nada" ? "Nada (solo consulta)" : `${configuracion.campos.length} ${configuracion.campos.length === 1 ? "campo" : "campos"}`;
    const acciones = [
        configuracion.puedeCrear && "crear",
        configuracion.puedeArchivar && "archivar",
        configuracion.puedeCatalogos && "catálogos",
    ].filter(Boolean);
    return { ve, edita, acciones: acciones.length ? acciones.join(", ") : "ninguna" };
}
